// copy-register: commercial
import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  capabilityMapAnchor,
  capabilityMapHref,
  groundworksLinksForPartnerCapability,
  partnerCapabilitiesInArea,
  partnerCapabilityAreas,
  partnerCapabilityLinksForTarget,
  resolvePartnerCapabilityMapping,
  type CapabilityMapView
} from "../capabilityMapping";
import { CapabilityMapping } from "../components/CapabilityMapping";
import { CapabilityOverview } from "../components/CapabilityOverview";
import { EmptyState, PageHeader, Trail, ordinal } from "../components/ui";
import { useCopy, useFramework } from "../framework";
import { useScrollToHash } from "./support";

const detailedHashPrefixes = [
  "#area-",
  "#capability-",
  "#workblock-",
  "#module-",
  "#shared-capability-"
] as const;

function usePrintMedia(): boolean {
  const [isPrint, setIsPrint] = useState(
    () => typeof window !== "undefined"
      && typeof window.matchMedia === "function"
      && window.matchMedia("print").matches
  );
  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const query = window.matchMedia("print");
    const sync = () => setIsPrint(query.matches);
    sync();
    query.addEventListener("change", sync);
    return () => query.removeEventListener("change", sync);
  }, []);
  return isPrint;
}

function ViewSwitch({ view, mapId }: { view: CapabilityMapView; mapId: string }) {
  const copy = useCopy();
  return (
    <nav className="apf-capability-map__views" aria-label={copy("partner-capability.view.label")}>
      {(["partner", "groundworks"] as const).map((candidate) => (
        <Link
          key={candidate}
          className="apf-capability-map__view"
          aria-current={candidate === view ? "page" : undefined}
          to={capabilityMapHref({ view: candidate, mapId })}
        >
          {copy(`partner-capability.view.${candidate}`)}
        </Link>
      ))}
    </nav>
  );
}

function MapSelector({ mapId }: { mapId: string }) {
  const { snapshot } = useFramework();
  const copy = useCopy();
  const [searchParams, setSearchParams] = useSearchParams();
  const mappings = snapshot.partnerCapabilityMappings ?? [];
  if (mappings.length < 2) return null;
  return (
    <label className="apf-capability-map__selector">
      <span>{copy("partner-capability.map.selector")}</span>
      <select
        value={mapId}
        onChange={(event) => {
          const next = new URLSearchParams(searchParams);
          next.set("map", event.target.value);
          next.delete("capability");
          setSearchParams(next);
        }}
      >
        {mappings.map((mapping) => <option value={mapping.id} key={mapping.id}>{copy(mapping.titleKey)}</option>)}
      </select>
    </label>
  );
}

function PartnerView({ mapId }: { mapId: string }) {
  const { snapshot } = useFramework();
  const copy = useCopy();
  const mapping = resolvePartnerCapabilityMapping(snapshot, mapId);
  if (!mapping) return null;
  return (
    <div className="apf-capability-map__areas">
      {partnerCapabilityAreas(mapping).map((area) => {
        const capabilities = partnerCapabilitiesInArea(mapping, area.id);
        return (
          <section
            className={`apf-capability-map__area${area.kind === "cross-cutting-area" ? " apf-capability-map__area--cross-cutting" : ""}`}
            id={capabilityMapAnchor("area", area.id)}
            key={area.id}
            aria-labelledby={`area-title-${area.id}`}
          >
            <header className="apf-capability-map__area-header">
              <span>{ordinal(area.order)}</span>
              <div>
                <p>{copy(area.kind === "cross-cutting-area" ? "partner-capability.area.cross-cutting" : "partner-capability.area.label")}</p>
                <h2 id={`area-title-${area.id}`}>{copy(area.labelKey)}</h2>
              </div>
              <strong>{capabilities.length} {copy(capabilities.length === 1 ? "partner-capability.count.singular" : "partner-capability.count.plural")}</strong>
            </header>
            <ol className="apf-capability-map__capabilities">
              {capabilities.map((node) => (
                <li className="apf-capability-map__capability" id={capabilityMapAnchor("capability", node.id)} key={node.id}>
                  <div className="apf-capability-map__capability-heading">
                    <span>{ordinal(node.order)}</span>
                    <h3>{copy(node.labelKey)}</h3>
                    <span className="apf-capability-map__fit-definition">
                      {copy(`partner-capability.fit.${node.groundworks.fit}`)}
                    </span>
                  </div>
                  <div className="apf-capability-map__targets">
                    {groundworksLinksForPartnerCapability(snapshot, mapping, node, copy).map((group) => (
                      <CapabilityMapping label={group.label} items={group.items} compact key={group.id} />
                    ))}
                  </div>
                </li>
              ))}
            </ol>
          </section>
        );
      })}
    </div>
  );
}

function GroundworksView({ mapId }: { mapId: string }) {
  const { snapshot } = useFramework();
  const copy = useCopy();
  const mapping = resolvePartnerCapabilityMapping(snapshot, mapId);
  if (!mapping) return null;
  const moduleById = new Map(snapshot.businessModuleRegistry.businessModules.map((module) => [module.id, module]));
  const domains = [...snapshot.content.domainWorkblocks].sort((left, right) => left.order - right.order);
  return (
    <div className="apf-capability-map__groundworks">
      <section aria-labelledby="groundworks-workblocks-title">
        <div className="apf-capability-map__section-heading">
          <p>{copy("partner-capability.groundworks.portfolio")}</p>
          <h2 id="groundworks-workblocks-title">{copy("partner-capability.groundworks.workblocks")}</h2>
        </div>
        {domains.map((domain) => (
          <article className="apf-capability-map__workblock" id={capabilityMapAnchor("domain-workblock", domain.id)} key={domain.id}>
            <header className="apf-capability-map__workblock-header">
              <span>{ordinal(domain.order)}</span>
              <div>
                <h3>{copy(domain.nameKey)}</h3>
                <p>{copy(domain.descriptorKey)}</p>
              </div>
              <CapabilityMapping
                label={copy("partner-capability.detail.heading")}
                items={partnerCapabilityLinksForTarget(snapshot, copy, "domain-workblock", domain.id)}
                compact
              />
            </header>
            <ol className="apf-capability-map__modules">
              {domain.businessModuleIds.flatMap((moduleId, index) => {
                const module = moduleById.get(moduleId);
                return module ? [(
                  <li id={capabilityMapAnchor("business-module", module.id)} key={module.id}>
                    <div className="apf-capability-map__module-heading">
                      <span>{ordinal(index + 1)}</span>
                      <div><h4>{copy(module.labelKey)}</h4><p>{copy(module.descriptorKey)}</p></div>
                    </div>
                    <CapabilityMapping
                      label={copy("partner-capability.detail.heading")}
                      items={partnerCapabilityLinksForTarget(snapshot, copy, "business-module", module.id)}
                      compact
                    />
                  </li>
                )] : [];
              })}
            </ol>
          </article>
        ))}
      </section>
      <section className="apf-capability-map__shared" aria-labelledby="groundworks-shared-title">
        <div className="apf-capability-map__section-heading">
          <p>{copy("partner-capability.groundworks.foundation")}</p>
          <h2 id="groundworks-shared-title">{copy("partner-capability.groundworks.shared")}</h2>
        </div>
        <ol>
          {snapshot.content.capabilities.map((capability, index) => (
            <li id={capabilityMapAnchor("shared-capability", capability.id)} key={capability.id}>
              <div className="apf-capability-map__shared-heading">
                <span>{ordinal(index + 1)}</span>
                <div><h3>{copy(capability.labelKey)}</h3><p>{copy(capability.descriptionKey)}</p></div>
              </div>
              <CapabilityMapping
                label={copy("partner-capability.detail.heading")}
                items={partnerCapabilityLinksForTarget(snapshot, copy, "shared-capability", capability.id)}
                compact
              />
            </li>
          ))}
        </ol>
      </section>
    </div>
  );
}

export function CapabilityMapPage() {
  const { snapshot } = useFramework();
  const copy = useCopy();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { hash } = location;
  /* A canonical node route such as /capability-map/capability-<id> carries
   * the same identity a fragment once did, and stays in the address bar. */
  const { nodeId } = useParams();
  const nodeAnchor = nodeId ? `#${nodeId}` : "";
  const effectiveHash = hash || nodeAnchor;
  const requestedMapId = searchParams.get("map");
  const mapping = resolvePartnerCapabilityMapping(snapshot, requestedMapId);
  const view: CapabilityMapView = searchParams.get("view") === "groundworks" ? "groundworks" : "partner";
  const selectedAreaId = searchParams.get("area") ?? undefined;
  const selectedCapabilityId = searchParams.get("capability")
    ?? (effectiveHash.startsWith("#capability-")
      ? decodeURIComponent(effectiveHash.slice("#capability-".length))
      : undefined);
  const detailedRequestKey = detailedHashPrefixes.some((prefix) => effectiveHash.startsWith(prefix))
    ? effectiveHash
    : searchParams.has("view")
      ? `view:${view}`
      : "";
  const [detailedOpen, setDetailedOpen] = useState(Boolean(detailedRequestKey));
  const isPrint = usePrintMedia();

  useEffect(() => {
    if (detailedRequestKey) setDetailedOpen(true);
  }, [detailedRequestKey]);

  useScrollToHash();

  useEffect(() => {
    if (!nodeId || hash) return;
    requestAnimationFrame(() => document.getElementById(nodeId)?.scrollIntoView({ block: "start" }));
  }, [nodeId, hash]);

  return (
    <div className="apf-page apf-register-reference apf-capability-map-page">
      <PageHeader
        eyebrow={copy("partner-capability.page.eyebrow")}
        title={copy("partner-capability.page.title")}
        description={copy("partner-capability.page.description")}
        meta={mapping ? <span>{copy(mapping.titleKey)}</span> : undefined}
      />
      {!mapping ? (
        <EmptyState
          title={copy("partner-capability.empty.title")}
          description={copy("partner-capability.empty.description")}
          action={<Link className="apf-button" to={capabilityMapHref({ view: "partner" })}>{copy("partner-capability.empty.action")}</Link>}
        />
      ) : (
        <>
          {(snapshot.partnerCapabilityMappings?.length ?? 0) > 1 ? (
            <div className="apf-capability-map__controls apf-capability-map__controls--map">
              <MapSelector mapId={mapping.id} />
            </div>
          ) : null}
          <aside className="apf-capability-map__disclaimer" aria-label={copy("partner-capability.disclaimer.label")}>
            {copy("partner-capability.page.disclaimer")}
          </aside>
          <CapabilityOverview
            snapshot={snapshot}
            mapping={mapping}
            copy={copy}
            selectedAreaId={selectedAreaId}
            selectedCapabilityId={selectedCapabilityId}
            onSelectedCapabilityChange={(capabilityId, areaId) => {
              const next = new URLSearchParams(searchParams);
              if (areaId) next.set("area", areaId);
              if (capabilityId) next.set("capability", capabilityId);
              else next.delete("capability");
              const nextSearch = next.toString();
              navigate(
                { pathname: location.pathname, search: nextSearch ? `?${nextSearch}` : "", hash: "" },
                { preventScrollReset: true }
              );
            }}
          />
          <details
            className="apf-capability-map__detailed"
            open={detailedOpen || isPrint}
            onToggle={(event) => {
              if (!isPrint) setDetailedOpen(event.currentTarget.open);
            }}
          >
            <summary>
              <span className="apf-capability-map__detailed-eyebrow">
                {copy("partner-capability.detailed.eyebrow")}
              </span>
              <span className="apf-capability-map__detailed-title" role="heading" aria-level={2}>
                {copy("partner-capability.detailed.title")}
              </span>
              <span className="apf-capability-map__detailed-description">
                {copy("partner-capability.detailed.description")}
              </span>
            </summary>
            <div className="apf-capability-map__detailed-body">
              <div className="apf-capability-map__controls">
                <ViewSwitch view={view} mapId={mapping.id} />
              </div>
              {view === "partner" ? <PartnerView mapId={mapping.id} /> : <GroundworksView mapId={mapping.id} />}
            </div>
          </details>
        </>
      )}
      <Trail
        label={copy("trail.label", "Where to go next")}
        items={[
          { to: "/workblocks", glyph: "object.workblock", label: copy("trail.work"), context: copy("trail.work.context") },
          { to: "/connected-work", glyph: "capability.integration", label: copy("trail.connected-work"), context: copy("trail.connected-work.context") }
        ]}
      />
    </div>
  );
}
